import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";
import { Badge, Button, confirmDelete, notifyError } from "../../../../components/ui";
import { usePatrolsStore } from "../../patrolsStore";
import { useBasesLocacionesStore } from "../../basesStore";
import { apiErrorMessage } from "../../services/basesApi";
import { INCIDENT_CATEGORIES, INCIDENT_STATUSES, SEVERITIES, type PatrolIncident } from "../../services/patrolsApi";
import { DetailDrawer, DetailRow, FilterSelect, FormField, Kpi, LoadBlock, PersonAvatar, Photo, PhotoGallery, SearchBox, SiteTree, Toolbar, inputCls, relativeTime, shortDateTime } from "../../components/console";
import { INCIDENT_TONES, SeverityBadge, treeMatch, useRondinesStatus, useRouteTree } from "./shared";

// Incidencias de rondín: severidad, foto de evidencia, estado y seguimiento.

type Draft = Pick<PatrolIncident, "title" | "description" | "category" | "severity" | "status" | "base_id" | "route_id" | "checkpoint_id" | "photos"> & { reportedBy?: { id: string; name: string } | null };

const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 } as const;

export default function RondinesIncidents() {
  const { t, i18n } = useTranslation("bases");
  const { incidents, routes, checkpoints, saveIncident, removeIncident } = usePatrolsStore();
  const bases = useBasesLocacionesStore((s) => s.bases);
  const { status, error, retry } = useRondinesStatus();
  const tree = useRouteTree();
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState<string>("");
  const [st, setSt] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft; files: File[] } | null>(null);
  const [saving, setSaving] = useState(false);
  const open = incidents.find((i) => i.id === openId) ?? null;
  const lng = i18n.language;

  const list = incidents
    .filter((i) => treeMatch(sel, i) && (!sev || i.severity === sev) && (!st || i.status === st))
    .filter((i) => !q || `${i.title} ${i.baseName} ${i.routeName} ${i.description}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      const openA = a.status === "open" || a.status === "in_progress" ? 0 : 1;
      const openB = b.status === "open" || b.status === "in_progress" ? 0 : 1;
      return openA - openB || SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.reportedAt.localeCompare(a.reportedAt);
    });
  const openCount = incidents.filter((i) => i.status === "open" || i.status === "in_progress").length;

  const setStatus = async (i: PatrolIncident, status: PatrolIncident["status"]) => {
    try {
      await saveIncident(i.id, { status });
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.draft.title.trim() || (!editing.draft.base_id && !editing.draft.route_id)) return void notifyError(t("rondines.incidents.required"));
    setSaving(true);
    try {
      const saved = await saveIncident(editing.id, editing.draft, editing.files);
      setEditing(null);
      setOpenId(saved.id);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (i: PatrolIncident) => {
    if (!(await confirmDelete({ title: t("rondines.incidents.deleteTitle", { name: i.title }), cancelText: t("common.cancel"), confirmText: t("common.delete") }))) return;
    try {
      await removeIncident(i.id);
      setOpenId(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  const patch = (p: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e));
  const newDraft = (): Draft => {
    const routeId = sel?.startsWith("route:") ? sel.slice(6) : "";
    const baseId = sel?.startsWith("base:") ? sel.slice(5) : routes.find((r) => r.id === routeId)?.base_id ?? bases[0]?.id ?? "";
    return { title: "", description: "", category: "other", severity: "medium", status: "open", base_id: baseId, route_id: routeId || null, checkpoint_id: null, photos: [], reportedBy: null };
  };

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("rondines.treeTitle")} allLabel={t("rondines.allRoutes")} nodes={tree} selected={sel} onSelect={setSel} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-alarm-warning-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("rondines.incidents.title")}</h1>
          </div>
          <SearchBox value={q} onChange={setQ} placeholder={t("rondines.incidents.search")} />
          <FilterSelect label={t("rondines.incidents.severity")} value={sev} onChange={setSev} options={SEVERITIES.map((s) => ({ value: s, label: t(`rondines.severity.${s}`) }))} />
          <FilterSelect label={t("fields.status")} value={st} onChange={setSt} options={INCIDENT_STATUSES.map((s) => ({ value: s, label: t(`rondines.incidentStatus.${s}`) }))} />
          <div className="ml-auto">
            <Button size="sm" icon={<i className="ri-add-line" />} disabled={!bases.length} onClick={() => setEditing({ id: null, draft: newDraft(), files: [] })}>
              {t("rondines.incidents.new")}
            </Button>
          </div>
        </Toolbar>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi icon="ri-alarm-warning-line" label={t("rondines.kpi.incidents")} value={openCount} tone={openCount ? "warning" : "success"} hint={t("rondines.incidents.total", { n: incidents.length })} />
            <Kpi icon="ri-fire-line" label={t("rondines.severity.critical")} value={incidents.filter((i) => i.severity === "critical" && i.status !== "closed" && i.status !== "resolved").length} tone="error" />
            <Kpi icon="ri-error-warning-line" label={t("rondines.severity.high")} value={incidents.filter((i) => i.severity === "high" && i.status !== "closed" && i.status !== "resolved").length} tone="warning" />
            <Kpi icon="ri-checkbox-circle-line" label={t("rondines.incidentStatus.resolved")} value={incidents.filter((i) => i.status === "resolved" || i.status === "closed").length} tone="success" />
          </div>
          <LoadBlock status={status} error={error} onRetry={retry} empty={incidents.length === 0} emptyIcon="ri-shield-check-line" emptyTitle={t("rondines.incidents.emptyTitle")} emptyText={t("rondines.incidents.emptyText")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {list.map((i) => (
                <button key={i.id} type="button" onClick={() => setOpenId(i.id)} className="overflow-hidden rounded-xl border border-line bg-card text-left transition-shadow hover:shadow-lg">
                  <div className="relative">
                    <Photo src={i.photos[0]} alt={i.title} thumb className="aspect-[16/9] w-full" icon="ri-alarm-warning-line" />
                    <span className="absolute left-2 top-2">
                      <SeverityBadge severity={i.severity} solid />
                    </span>
                    <span className="absolute right-2 top-2">
                      <Badge size="sm" variant="solid" tone={INCIDENT_TONES[i.status]}>
                        {t(`rondines.incidentStatus.${i.status}`)}
                      </Badge>
                    </span>
                  </div>
                  <div className="space-y-1 p-3">
                    <div className="truncate text-sm font-semibold text-content">{i.title}</div>
                    <div className="truncate text-xs text-content-secondary">
                      {i.baseName}
                      {i.routeName ? ` · ${i.routeName}` : ""}
                    </div>
                    <div className="flex items-center justify-between pt-1 text-[11px] text-content-muted">
                      <span>{t(`rondines.category.${i.category}`)}</span>
                      <span>{relativeTime(i.reportedAt, lng)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {list.length === 0 ? <p className="py-8 text-center text-sm text-content-muted">{t("list.noMatches")}</p> : null}
          </LoadBlock>
        </div>
      </div>

      <DetailDrawer
        open={Boolean(open)}
        onClose={() => setOpenId(null)}
        title={open?.title}
        subtitle={open ? `${open.baseName ?? ""}${open.routeName ? ` · ${open.routeName}` : ""}` : null}
        actions={
          open ? (
            <>
              <Button size="sm" variant="soft" icon={<i className="ri-edit-line" />} onClick={() => setEditing({ id: open.id, draft: { title: open.title, description: open.description ?? "", category: open.category, severity: open.severity, status: open.status, base_id: open.base_id, route_id: open.route_id ?? null, checkpoint_id: open.checkpoint_id ?? null, photos: open.photos }, files: [] })}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="dangerSoft" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => void remove(open)} />
            </>
          ) : null
        }
      >
        {open ? (
          <div className="space-y-4">
            <PhotoGallery photos={open.photos} alt={open.title} />
            <div className="flex flex-wrap items-center gap-2">
              <SeverityBadge severity={open.severity} />
              <Badge tone={INCIDENT_TONES[open.status]} dot>
                {t(`rondines.incidentStatus.${open.status}`)}
              </Badge>
              <Badge>{t(`rondines.category.${open.category}`)}</Badge>
            </div>
            {open.description ? <p className="text-sm text-content">{open.description}</p> : null}
            <div className="divide-y divide-line rounded-xl border border-line bg-card px-3">
              <DetailRow icon="ri-time-line" label={t("rondines.incidents.reported")}>
                {shortDateTime(open.reportedAt, lng)} · {relativeTime(open.reportedAt, lng)}
              </DetailRow>
              <DetailRow icon="ri-user-line" label={t("rondines.incidents.reportedBy")}>
                <span className="flex items-center gap-2">
                  {open.reportedBy?.name ? <PersonAvatar name={open.reportedBy.name} size="sm" /> : null}
                  {open.reportedBy?.name ?? "—"}
                </span>
              </DetailRow>
              <DetailRow icon="ri-map-pin-line" label={t("rondines.incidents.checkpoint")}>{open.checkpointName || checkpoints.find((c) => c.id === open.checkpoint_id)?.name || "—"}</DetailRow>
              <DetailRow icon="ri-user-settings-line" label={t("rondines.incidents.assignee")}>{open.assignee?.name ?? "—"}</DetailRow>
              {open.resolvedAt ? (
                <DetailRow icon="ri-checkbox-circle-line" label={t("rondines.incidents.resolvedAt")}>
                  {shortDateTime(open.resolvedAt, lng)} {open.resolution ? `· ${open.resolution}` : ""}
                </DetailRow>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {open.status === "open" ? (
                <Button size="sm" variant="warningSoft" icon={<i className="ri-tools-line" />} onClick={() => void setStatus(open, "in_progress")}>
                  {t("rondines.incidents.attend")}
                </Button>
              ) : null}
              {open.status === "open" || open.status === "in_progress" ? (
                <Button size="sm" variant="successSoft" icon={<i className="ri-check-line" />} onClick={() => void setStatus(open, "resolved")}>
                  {t("rondines.incidents.resolve")}
                </Button>
              ) : null}
              {open.status === "resolved" ? (
                <Button size="sm" variant="soft" icon={<i className="ri-archive-line" />} onClick={() => void setStatus(open, "closed")}>
                  {t("rondines.incidents.close")}
                </Button>
              ) : null}
              {open.status === "resolved" || open.status === "closed" ? (
                <Button size="sm" variant="ghost" icon={<i className="ri-arrow-go-back-line" />} onClick={() => void setStatus(open, "open")}>
                  {t("rondines.incidents.reopen")}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </DetailDrawer>

      {editing ? (
        <Modal
          title={editing.id ? t("rondines.incidents.edit") : t("rondines.incidents.new")}
          onClose={() => setEditing(null)}
          content={
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label={t("rondines.incidents.titleField")} className="sm:col-span-3">
                <input className={inputCls} value={editing.draft.title} onChange={(e) => patch({ title: e.target.value })} />
              </FormField>
              <FormField label={t("fields.base")}>
                <select className={inputCls} value={editing.draft.base_id} onChange={(e) => patch({ base_id: e.target.value, route_id: null, checkpoint_id: null })}>
                  {bases.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.route")}>
                <select className={inputCls} value={editing.draft.route_id ?? ""} onChange={(e) => patch({ route_id: e.target.value || null, checkpoint_id: null })}>
                  <option value="">—</option>
                  {routes.filter((r) => r.base_id === editing.draft.base_id).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.incidents.checkpoint")}>
                <select className={inputCls} value={editing.draft.checkpoint_id ?? ""} onChange={(e) => patch({ checkpoint_id: e.target.value || null })}>
                  <option value="">—</option>
                  {checkpoints.filter((c) => c.route_id === editing.draft.route_id).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.order}. {c.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.incidents.severity")}>
                <select className={inputCls} value={editing.draft.severity} onChange={(e) => patch({ severity: e.target.value as Draft["severity"] })}>
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {t(`rondines.severity.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.incidents.category")}>
                <select className={inputCls} value={editing.draft.category} onChange={(e) => patch({ category: e.target.value as Draft["category"] })}>
                  {INCIDENT_CATEGORIES.map((s) => (
                    <option key={s} value={s}>
                      {t(`rondines.category.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.status")}>
                <select className={inputCls} value={editing.draft.status} onChange={(e) => patch({ status: e.target.value as Draft["status"] })}>
                  {INCIDENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`rondines.incidentStatus.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.description")} className="sm:col-span-3">
                <textarea className={inputCls} rows={3} value={editing.draft.description ?? ""} onChange={(e) => patch({ description: e.target.value })} />
              </FormField>
              <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                {editing.draft.photos.map((p, k) => (
                  <div key={`${p}-${k}`} className="relative">
                    <Photo src={p} alt={editing.draft.title} thumb className="h-14 w-20 rounded-lg" />
                    <button type="button" aria-label={t("common.delete")} onClick={() => patch({ photos: editing.draft.photos.filter((_, j) => j !== k) })} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-error text-white">
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ))}
                {editing.files.map((f, k) => (
                  <span key={`${f.name}-${k}`} className="rounded-lg border border-dashed border-line px-2 py-1 text-[11px] text-content-secondary">
                    {f.name}
                  </span>
                ))}
                <label className="cursor-pointer rounded-lg border border-dashed border-primary-500/60 px-3 py-2 text-xs text-primary-500 hover:bg-primary-500/10">
                  <i className="ri-camera-line" /> {t("rondines.incidents.evidence")}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setEditing((x) => (x ? { ...x, files: [...x.files, ...Array.from(e.target.files ?? [])] } : x))} />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-3">
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  {t("common.cancel")}
                </Button>
                <Button loading={saving} onClick={() => void save()} icon={<i className="ri-save-line" />}>
                  {t("common.save")}
                </Button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
